import type { CSSProperties } from 'react';
import type { BookSlotName } from '../book-slot';

/**
 * Block 1 · 1.4 · Decorative side image slots (Sebastian ruling
 * 2026-09-09).
 *
 * Two decorative panels — left and right of the centre lane — that
 * change per screen. Files land later in `/pwa/public/`; the slots
 * ship now so the responsive behaviour is in place when the images
 * appear.
 *
 * ═══ RESPONSIVE BEHAVIOUR (spec 1.4) ═══════════════════════════════
 *
 *   ≥1280 px (Tailwind `xl:`)   both images visible
 *   768-1280 px  (Tailwind `md:`) right image only
 *   <768 px                       neither, and the file is NOT loaded
 *
 * `hidden md:block` / `hidden xl:block` gates the DOM node with
 * `display: none`. Every modern browser skips loading a
 * `background-image` on a `display: none` element — so the phone
 * viewport never fires a network request for the file, per spec.
 *
 * ═══ FAIL-SILENT LOAD (spec 1.4) ═══════════════════════════════════
 *
 * Missing file → renders NOTHING. Implemented via CSS
 * `background-image: url(...)`, not `<img>`: when the URL 404s the
 * browser silently paints nothing. No broken-image icon, no alt
 * text bubble, no console noise at the customer's end (DevTools
 * network tab shows the 404, which is fine for the pre-file phase).
 *
 * No layout shift when the file later appears: the panels are
 * `position: fixed`, sized to the viewport, and live OUTSIDE the
 * content flow. The centre lane never shrinks and never widens as
 * a function of these panels; when the file finally lands the
 * decoration simply appears in the empty side gutter.
 *
 * ═══ STICKY BEHAVIOUR ═══════════════════════════════════════════════
 *
 * `position: fixed` pins the panels to the viewport as the customer
 * scrolls — "they travel with the scroll rather than scrolling
 * away" per Sebastian's Block 1 note. `pointer-events-none` +
 * `aria-hidden` keep them purely decorative: no click intercept,
 * no announcement to screen readers, empty alt.
 *
 * ═══ SOFT MASK FADING TOWARD THE CENTRE ═════════════════════════════
 *
 * `mask-image` fades each panel toward the centre lane so the
 * image edge does not have to match the page colour exactly. My
 * call on the exact fade percentages (0-55 % solid, 55-100 %
 * fade-out); correct by eye later.
 */

// One rule for all six slots (Sebastian ruling 2026-09-12): each slot
// reads `/bg-<slot>-<side>.webp` from pwa/public/. WebP at 800 px wide,
// quality 78, both files under 160 KB (soft target — figuren-rechts
// ships at 152.6 KB, others in the 78-136 KB band; ceiling bumped
// 150 → 160 in 2026-09-16 to reflect the shipped set). Files ship
// UNMUTED — the CSS damp at lines 103-104 + 142-143 mutes at render
// (saturate 70 %, opacity 80 %); do NOT bake the mute into the file
// bytes, or the two damp constants stop being the single source of
// truth. Missing files render nothing via CSS background-image's
// fail-silent 404 (see file JSDoc §"FAIL-SILENT LOAD").
const SLOT_FILE: Record<BookSlotName, { left: string; right: string }> = {
  personen: { left: '/bg-personen-links.webp', right: '/bg-personen-rechts.webp' },
  geschichte: { left: '/bg-geschichte-links.webp', right: '/bg-geschichte-rechts.webp' },
  figuren: { left: '/bg-figuren-links.webp', right: '/bg-figuren-rechts.webp' },
  vorschau: { left: '/bg-vorschau-links.webp', right: '/bg-vorschau-rechts.webp' },
  bestellung: { left: '/bg-bestellung-links.webp', right: '/bg-bestellung-rechts.webp' },
  freigabe: { left: '/bg-freigabe-links.webp', right: '/bg-freigabe-rechts.webp' },
};

/**
 * ═══ TUNABLES — one place for Sebastian to point at ═══════════════
 *
 * All the dials that shape how the panels sit on screen. Change the
 * numbers here; the render below reads them verbatim.
 *
 * Applied via INLINE style (not Tailwind arbitrary classes) because
 * an earlier `w-[18vw] max-w-[22rem]` shape appeared to render at
 * a much larger width than the arithmetic predicted (Sebastian
 * report 2026-09-09 pilot: "closer to 700 px each on a wide
 * viewport"). Inline style guarantees the browser gets exactly the
 * values below regardless of Tailwind's arbitrary-value parsing.
 */

/** Panel width as a percentage of viewport width.
 *  Sebastian 2026-09-09: two steps of +4 each — 18 → 22 → 26. Second
 *  step is the last panel tune tonight. Combined with the max-cap
 *  bump below, effective panel width is now 374 / 480 at 1440 / 1920. */
const PANEL_WIDTH_VW = 26;
/** Hard cap on panel width so an ultrawide monitor doesn't spread
 *  the image across half the screen.
 *  Sebastian 2026-09-09: two steps of +4 each — 22 → 26 → 30. Cap
 *  30rem = 480 px kicks in at 26vw × ~1846 px viewport (26 × 18.46
 *  = 480). At 1920 the panel lands exactly on the cap. */
const PANEL_MAX_WIDTH_REM = 30;
/** Fade start — 0 %-to-this-percent is solid image, then fades to
 *  transparent at 100 %. Higher = narrower fade, more solid image.
 *  Sebastian pilot 2026-09-09: 80 left a visible hard edge where the
 *  panel met the centre lane; drop to 60 so the last 40 %% of the
 *  panel width is a gradient (double the previous fade band). */
const FADE_SOLID_PERCENT = 60;
/** Damp values (Sebastian pilot 2026-09-09) — panels are decoration,
 *  must read as background not as a second thing to look at. NO
 *  white overlay (flattens the artwork); NO blur (keep image sharp).
 *  Desaturation + slight transparency preserves depth, drops
 *  colour punch. */
const SATURATE_PERCENT = 70;
const OPACITY_PERCENT = 80;

/** Space above the panel (accounts for the sticky navy header). */
const PANEL_TOP_CLASS = 'top-20';

/**
 * Background-size mode. `'cover'` = image scales to fill the panel
 * and crops whichever dimension overflows. `'contain'` = the whole
 * image fits inside the panel (no crop) at the cost of visible
 * letterbox edges.
 *
 * Sebastian review 2026-09-09 (second pilot pair) — `contain` was
 * tried and reverted; a small tile floating in the panel with hard
 * edges above and below reads worse than a cover-crop of the
 * outer edge. Panel aspect is ≈ 1:3 portrait; the pilot sources
 * were 2:3 which forced a large horizontal crop. Sebastian is
 * producing the final twelve in a taller ratio so `cover` crops
 * almost nothing.
 */
const PANEL_BACKGROUND_SIZE: 'contain' | 'cover' = 'cover';

const PANEL_BASE_CLASS = `fixed ${PANEL_TOP_CLASS} bottom-0 z-0 bg-no-repeat pointer-events-none`;

export interface BookSideImagesProps {
  readonly slot: BookSlotName | null;
}

export function BookSideImages({ slot }: BookSideImagesProps) {
  if (slot === null) return null;
  const files = SLOT_FILE[slot];

  // Shared inline-style bits (width / max-width / damp filter /
  // opacity). Left + right panels differ only in bg-image URL,
  // background-position anchor, and the mask-fade direction.
  const sharedStyle: CSSProperties = {
    width: `${PANEL_WIDTH_VW}vw`,
    maxWidth: `${PANEL_MAX_WIDTH_REM}rem`,
    backgroundSize: PANEL_BACKGROUND_SIZE,
    filter: `saturate(${SATURATE_PERCENT}%)`,
    opacity: OPACITY_PERCENT / 100,
  };

  const leftMask = `linear-gradient(to right, black 0%, black ${FADE_SOLID_PERCENT}%, transparent 100%)`;
  const rightMask = `linear-gradient(to left, black 0%, black ${FADE_SOLID_PERCENT}%, transparent 100%)`;

  return (
    <>
      {/* Left panel — visible only at ≥1280 (xl:). Below xl the DOM
          node is display:none and the browser skips the fetch.
          background-position: left center anchors the artwork to
          the OUTER (screen-left) edge so the crop happens toward
          the centre lane, not against the screen edge. */}
      <div
        aria-hidden
        className={`${PANEL_BASE_CLASS} left-0 hidden xl:block`}
        style={{
          ...sharedStyle,
          backgroundImage: `url("${files.left}")`,
          backgroundPosition: 'left center',
          WebkitMaskImage: leftMask,
          maskImage: leftMask,
        }}
      />
      {/* Right panel — visible at ≥768 (md:) and up. Symmetric:
          background-position: right center anchors artwork to the
          outer (screen-right) edge. */}
      <div
        aria-hidden
        className={`${PANEL_BASE_CLASS} right-0 hidden md:block`}
        style={{
          ...sharedStyle,
          backgroundImage: `url("${files.right}")`,
          backgroundPosition: 'right center',
          WebkitMaskImage: rightMask,
          maskImage: rightMask,
        }}
      />
    </>
  );
}
